export const dataLayerPageError = (properties: {
  type: 'crash' | 'custom'
  message: string
}) => {
  window.dataLayer?.push({
    event: 'page-error',
    error_type: properties.type,
    error_message: properties.message
  })
}

export const dataLayerSignupEvent = (properties: {
  authType: 'email' | 'google'
  email?: string
  firstName?: string
  lastName?: string
}): void => {
  window.dataLayer?.push({
    event: 'signup',
    authType: properties.authType,
    authChannel: properties.authType === 'email' ? 'email' : 'social',
    email: properties.email,
    firstName: properties.firstName,
    lastName: properties.lastName
  })
}

export const dataLayerLoginEvent = (properties: {
  authType: 'email' | 'google'
  email?: string
}): void => {
  window.dataLayer?.push({
    event: 'login',
    authType: properties.authType,
    authChannel: properties.authType === 'email' ? 'email' : 'social',
    email: properties.email
  })
}

export const dataLayerLogoutEvent = (): void => {
  window.dataLayer?.push({
    event: 'logout',
    email: undefined,
    name: undefined,
    role: undefined,
    phone: undefined,
    userId: undefined
  })
}

export const dataLayerSubscriptionEvent = (properties: {
  planKey: string
  planName: string
  planStatus: string
}) => {
  window.dataLayer?.push({
    event: 'subscription-status',
    ...properties
  })
}

export const dataLayerTypeformEvent = (
  properties: Record<string, string | undefined>
): void => {
  window.dataLayer?.push({
    formValues: undefined
  })
  window.dataLayer?.push({
    event: 'order-typeform-submit',
    formValues: properties
  })
}

/* events to be added in gtm and call in codebase */
export const dataLayerDashboardEvent = (properties: {
  totalProjects: number
  totalContentPieces: number
  pendingApprovals: number
  arrivingSoon: number
  inRework: number
}): void => {
  window.dataLayer?.push({
    event: 'dashboard',
    ...properties
  })
}

export const dataLayerProjectCreationInitiatedEvent = ({
  source,
  ...rest
}: {
  projectName: string
  contentCategory: string
  contentType: string
  industry: string
  contentLanguage: string
  source?: string
  skillLevel: string
}): void => {
  window.dataLayer?.push({
    event: 'project-creation',
    ...rest,
    source: source ?? 'Web'
  })
}

export const dataLayerTopicsAndAssignmentsEvent = (properties: {
  contentTitle: string
  wordCount: number
}): void => {
  window.dataLayer?.push({
    event: 'topics-and-assignment',
    ...properties
  })
}

export const dataLayerCheckoutEvent = ({
  source,
  ...rest
}: {
  orderCategory: string
  industry?: string
  creatorSkill?: string
  contentLanguage: string
  estimatedDelivery: string
  contentCategory?: string
  source?: string
  itemQuantity: number
  registeredName: string
  zipCode: string
  state: string
  country: string
  city: string
  ecommerce: {
    checkout: {
      actionField: {
        id: string // Transaction ID or order id. Required for purchases and refunds.
        revenue: number // Total transaction value (incl. tax and shipping)
        tax: number
        coupon?: string
      }
    }
  }
}): void => {
  window.dataLayer?.push({
    event: 'checkout',
    ...rest,
    source: source ?? 'Web'
  })
}

export const dataLayerTransactionSuccessEvent = (properties: {
  projectName: string
  orderCategory: string
  contentType: string
  industry: string
  creatorSkill: string
  contentLanguage: string
  itemQuantity: number
  currencyCode: string
  ecommerce: {
    purchase: {
      actionField: {
        id: string // Transaction ID or order id. Required for purchases and refunds.
        revenue: number // Total transaction value (incl. tax and shipping)
        tax: number

        affiliation: ''
        shipping: ''
        coupon?: string
      }
      products: [
        {
          name?: string
          id: string
          price: string
          category?: string
          quantity?: number
          coupon: string
          brand: 'peppercontent'
          variant: ''
        }
      ]
    }
  }
  vendorOrderId: string
}): void => {
  window.dataLayer?.push({
    event: 'transaction-success',
    ...properties
  })
}

export const dataLayerTransactionFailedEvent = (properties: {
  // email: string // available
  /* Cannot access mobile number and payment mode */
  // mobileNumber: string
  // paymentMode: string
  orderCategory: string
  contentType: string
  industry: string
  creatorSkill: string
  contentLanguage: string
  itemQuantity: number
  ecommerce: {
    currencyCode: string
    purchase: {
      actionField: {
        id: string // Transaction ID or order id. Required for purchases and refunds.
        revenue: number // Total transaction value (incl. tax and shipping)
        tax: number
      }
    }
  }
  vendorOrderId: string
}): void => {
  window.dataLayer?.push({
    event: 'transaction-failed',
    ...properties
  })
}

export const dataLayerProjectDetailsEvent = (properties: {
  projectName: string
  projectVertical: string
  projectGenre: string
  contentLanguage?: string
  creatorExperience: string
  projectId: string
  wordCount: number
  noOfAssignment: number
  dateAssigned: string
  projectAudience?: string
  deadline: string
}): void => {
  window.dataLayer?.push({
    event: 'project-details',
    ...properties
  })
}

export const dataLayerProjectDraftEvent = (properties: {
  projectName: string
  projectGenre: string
  projectVertical: string
  projectCategory: string
}): void => {
  window.dataLayer?.push({
    event: 'project-draft-created',
    ...properties
  })
}

export const dataLayerProfileDetailsChangedEvent = (properties: {
  firstName: string
  lastName: string
  role: string
  phone: string
  email: string
}): void => {
  window.dataLayer?.push({
    event: 'profile-details-changed',
    ...properties
  })
}

export const dataLayerPasswordChangedEvent = (properties: {
  pageName: string
}): void => {
  window.dataLayer?.push({
    event: 'password-changed',
    ...properties
  })
}

export const dataLayerOrganizationBusinessEvent = (properties: {
  organizationName: string
  sendEmailUpdates: boolean
  sendProjectUpdates: boolean
  sendAssignmentUpdates: boolean
  sendWorkflowUpdates: boolean
}): void => {
  window.dataLayer?.push({
    event: 'organization-business',
    ...properties
  })
}

export const dataLayerStandardClientEvent = (properties: {
  organizationName: string
  sendEmailUpdates: boolean
  sendProjectUpdates: boolean
  sendAssignmentUpdates: boolean
  sendWorkflowUpdates: boolean
}): void => {
  window.dataLayer?.push({
    event: 'standard-client',
    ...properties
  })
}

export const dataLayerOrganizationDetailsChangeEvent = (properties: {
  organizationName: string
  individual: boolean
  primaryIndustry: string
  businessType?: string
  companySize?: number
  website?: string
}): void => {
  window.dataLayer?.push({
    event: 'organization-details-change',
    ...properties
  })
}

export const dataLayerBillingChangeEvent = (properties: {
  organizationName: string
  address: string
  state: string
  city: string
  pincode: string
  country: string
}): void => {
  window.dataLayer?.push({
    event: 'billing-details',
    ...properties
  })
}

export const dataLayerAddTeamMemberEvent = (properties: {
  newTeamMember:
    | {
        email: string
        accessLevel: TeamMemberRole
      }
    | undefined
}): void => {
  window.dataLayer?.push({
    newTeamMember: undefined
  })
  window.dataLayer?.push({
    event: 'add-team-member',
    newTeamMember: properties.newTeamMember
  })
}

export const dataLayerProjectSearchEvent = ({
  projectName
}: {
  projectName: string
}): void => {
  window.dataLayer?.push({
    event: 'project-search',
    projectName
  })
}

export const dataLayerCtaClickEvent = (properties: {
  sectionName: string
  ctaName: string
}): void => {
  window.dataLayer?.push({
    event: 'cta-click',
    pageName: document.title,
    ctaName: properties.ctaName,
    sectionName: properties.sectionName
  })
}

export const dataLayerProjectCreatedEvent = (properties: {
  category?: string
  verticle?: string
  userEmail?: string
  companyName?: string
}): void => {
  window.dataLayer?.push({
    event: 'project-created',
    ...properties
  })
}

export const dataLayerProjectStepEvent = (properties: {
  stepName: string
  projectName?: string
  industry?: string //genre-id
  targetReadership?: string
}): void => {
  window.dataLayer?.push({
    event: `createProjectStep`,
    ...properties
  })
}

export const dataLayerDuplicateProjectDraftCreatedEvent = (properties: {
  draftId: string
  originalProjectId: string
}): void => {
  window.dataLayer?.push({
    event: 'duplicate-project-draft-created',
    ...properties
  })
}

export const dataLayerDuplicateProjectCreatedEvent = (properties: {
  projectId: string
  originalProjectId: string
}): void => {
  window.dataLayer?.push({
    event: 'duplicate-project-created',
    ...properties
  })
}

//when one clicks on create a project button
export const dataLayerProjectCreationInitiateEvent = (): void => {
  window.dataLayer?.push({
    event: 'newProjectClick'
  })
}

export const dataLayerVerifyOtpEvent = (): void => {
  window.dataLayer?.push({
    event: `otp-verify`
  })
}

export const dataLayerEnableMultiCategoryEvent = (properties: {
  rootVertical: string
  optedVertical: string
}): void => {
  window.dataLayer?.push({
    event: 'multiCategoryEnabled',
    ...properties
  })
}

//** Creator Platform Events */
export const dataLayerSetUpAccountSaveAndExitEvent = (): void => {
  window.dataLayer?.push({
    event: `setup-account-save-and-exit`
  })
}
